import React from 'react';
import OsScroll from '../ui/OsScroll';
import { AlertTriangle, HelpCircle, Check, Trash2, X } from 'lucide-react';
import { visibleEscapes } from '../../utils/visibleEscapes';
import { aiMayReadResponses } from '../../services/ai/responseAccess';
import { useApprovalTarget } from './approvalTargets';

/**
 * The in-app assistant's approval card (Auto-run off, or always for a
 * destructive tool).
 *
 * **Whatever the tool will act on is shown IN FULL.** Until 13.09.2026 the
 * label was `Run on ${conn}: ${command.slice(0, 120)}` (and SQL at 80, a fetched
 * URL at 120) while the executor ran the unmodified input — a benign prefix
 * could be approved with an arbitrary tail. Raising the limit would only move
 * the cliff, so there is no limit: the payload goes into a `pre` that wraps and
 * scrolls, and control characters are shown as visible escapes so a `\r` or an
 * ANSI clear cannot hide part of it. Same rules as `McpTerminalApprovalModal`.
 *
 * `describeApproval` is pure so the "nothing is shortened" property can be
 * tested for every tool without rendering the chat panel.
 */

export const DESTRUCTIVE_TOOLS = Object.freeze(['delete_document', 'delete_board']);

/**
 * The fields `aiUpdateRequest` (hooks/useAiActions.js) actually applies. The
 * card lists these from the INPUT — never the model-supplied `updated_fields`,
 * which the executor ignores: a model could send `updated_fields: ['name']`
 * alongside a new `url` and headers, and the card would have read "Update name"
 * (13.09.2026 review B2).
 */
export const UPDATE_REQUEST_FIELDS = Object.freeze(['name', 'method', 'url', 'headers', 'body', 'mock']);

const asText = (v) => {
  if (typeof v === 'string') return v;
  try { return JSON.stringify(v, null, 2); } catch { return String(v); }
};

/**
 * @param {{ target?: string|null }} [context] WHERE it will run, resolved by
 *   the component (`./approvalTargets`) and passed in so this stays pure.
 * @returns {{ title: string, text: string, payload: string|null, destructive: boolean }}
 *   `payload` is the exact string the tool will act on (command, SQL, URL),
 *   rendered verbatim in a code block; `text` is the sentence around it.
 */
export function describeApproval(toolName, toolInput, context) {
  const input = toolInput || {};
  const target = context?.target || null;
  const destructive = DESTRUCTIVE_TOOLS.includes(toolName);
  const title = destructive
    ? (toolName === 'delete_board' ? 'Delete board?' : 'Delete document?')
    : toolName === 'write_document' ? 'Overwrite document?'
      : 'Run this action?';
  const str = (v) => (typeof v === 'string' ? v : v == null ? '' : String(v));

  let text;
  let payload = null;
  switch (toolName) {
    case 'get_last_response':
      // Says what will actually cross (review N2): with the privacy switch off
      // the body is withheld and only the status is sent.
      text = aiMayReadResponses()
        ? 'Send the last response body (redacted) to the model so it can write tests against it'
        : 'Send the last response status to the model ("Let AI read responses" is off, so no body)';
      break;
    case 'terminal_run_command':
      text = `Run on ${target || str(input.conn_id) || 'the server'}:`;
      payload = str(input.command);
      break;
    case 'db_run_sql': {
      // Until 17.09.2026 this read "Run SQL:" and never said on WHICH database.
      const where = target || str(input.conn_id);
      text = where ? `Run SQL on ${where}:` : 'Run SQL:';
      payload = str(input.sql);
      break;
    }
    case 'fetch_url':
      // A model-chosen URL fetched from the user's machine — show all of it.
      text = 'Fetch this URL and share its text with the model:';
      payload = str(input.url);
      break;
    case 'run_collection':
      text = `Run collection ${str(input.collection_name || input.collection_id)}`;
      break;
    case 'send_request':
      if (input.use_current_tab) {
        text = 'Resend the active request tab';
      } else if (input.url) {
        text = `Send ${str(input.method) || 'GET'}:`;
        payload = str(input.url);
      } else {
        text = `Send ${str(input.method) || 'GET'} ${input.request_id ? `request ${str(input.request_id)}` : ''}`.trim();
      }
      break;
    case 'delete_document':
      text = 'This document will be permanently deleted.';
      break;
    case 'delete_board':
      text = `${input.board_name ? `“${str(input.board_name)}”` : 'This board'} and all its tasks will be permanently deleted.`;
      break;
    case 'write_document':
      text = 'This replaces the current document body.';
      break;
    case 'update_request': {
      const fields = UPDATE_REQUEST_FIELDS.filter((k) => input[k] !== undefined);
      text = `Update ${fields.join(', ') || 'nothing'} on request ${str(input.request_id)}`.trim();
      // Every value that will be written, in full.
      payload = fields.length ? fields.map((k) => `${k}: ${asText(input[k])}`).join('\n') : null;
      break;
    }
    case 'set_auth':
      text = `Set ${str(input.type) || 'auth'} authentication on request ${str(input.request_id)}`.trim();
      payload = ['token', 'username', 'password', 'key', 'value', 'add_to']
        .filter((k) => input[k] !== undefined)
        .map((k) => `${k}: ${asText(input[k])}`)
        .join('\n') || null;
      break;
    case 'import_spec':
      if (input.url) {
        text = 'Fetch and import this API spec:';
        payload = str(input.url);
      } else {
        text = `Import a pasted API spec${input.content ? ` (${str(input.content).length} characters)` : ''}`;
      }
      break;
    default:
      text = `Run ${toolName}`;
  }
  return { title, text, payload, destructive };
}

export default function AiApprovalCard({ toolName, toolInput, awaitingConfirm, onConfirm }) {
  const target = useApprovalTarget(toolName, toolInput);
  const { title, text, payload, destructive } = describeApproval(toolName, toolInput, { target });
  const Icon = destructive ? AlertTriangle : HelpCircle;
  return (
    <div className={`ai-chat-confirm${destructive ? ' ai-chat-confirm--danger' : ''}`}>
      <div className="ai-chat-confirm-title">
        <Icon size={13} className="flex-shrink-0" />
        <span>{title}</span>
      </div>
      <div className="ai-chat-confirm-desc">{text}</div>
      {payload !== null && (
        <OsScroll className="ai-chat-confirm-payload">
          <pre data-testid="ai-approval-payload">{visibleEscapes(payload)}</pre>
        </OsScroll>
      )}
      <div className="ai-chat-confirm-actions">
        <button
          type="button"
          disabled={!awaitingConfirm}
          onClick={() => onConfirm?.(true)}
          className={`ai-chat-confirm-btn ${destructive ? 'ai-chat-confirm-btn--danger' : 'ai-chat-confirm-btn--primary'}`}
        >
          {destructive ? <Trash2 size={12} /> : <Check size={12} />}
          {destructive ? 'Delete' : 'Confirm'}
        </button>
        <button
          type="button"
          disabled={!awaitingConfirm}
          onClick={() => onConfirm?.(false)}
          className="ai-chat-confirm-btn ai-chat-confirm-btn--cancel"
        >
          <X size={12} /> Cancel
        </button>
      </div>
    </div>
  );
}
